import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Icon } from "@iconify/react/dist/iconify.js";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import axios from "axios";
import { API_URL, API_URL_LOCAL } from "..";

// Mapbox token (hardcoded as provided; use environment variable in production)
mapboxgl.accessToken = "pk.eyJ1IjoiemFtcCIsImEiOiJjbTkxa3dkcnMwMmQxMnJzYzBobW9hOHBiIn0.GWvvqPrWqtMby0HYCYdjMQ";

// Reusable Input Component
const FormInput = ({ label, name, type, value, onChange, error, required, placeholder }) => (
  <div className="mb-20">
    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
      {label} {required && <span className="text-danger-600">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className="form-control radius-8"
      placeholder={placeholder}
    />
    {error && <small className="text-danger-600">{error}</small>}
  </div>
);

// Reusable Select Component
const FormSelect = ({ label, name, value, onChange, options, error, required }) => (
  <div className="mb-20">
    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
      {label} {required && <span className="text-danger-600">*</span>}
    </label>
    <select name={name} value={value} onChange={onChange} className="form-control radius-8">
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    {error && <small className="text-danger-600">{error}</small>}
  </div>
);

const AddLocationPage = () => {
  const [formValues, setFormValues] = useState({
    name: "",
    address: "",
    timezone: "",
    geofence_enabled: false,
    geofence_radius: "",
    latitude: null,
    longitude: null,
  });
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const geocoderContainer = useRef(null);

  // List of timezones (simplified; use moment-timezone for a complete list)
  const timezoneOptions = [
    { value: "", label: "--SELECT TIMEZONE--" },
    { value: "UTC", label: "UTC" },
    { value: "America/New_York", label: "America/New_York (Eastern Time)" },
    { value: "America/Chicago", label: "America/Chicago (Central Time)" },
    { value: "America/Los_Angeles", label: "America/Los_Angeles (Pacific Time)" },
    { value: "Europe/London", label: "Europe/London" },
    { value: "Asia/Karachi", label: "Asia/Karachi" },
    { value: "Asia/Tokyo", label: "Asia/Tokyo" },
  ];

  // Initialize Mapbox map
  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [-74.0060, 40.7128], // Default: New York
      zoom: 10,
    });

    map.current.addControl(new mapboxgl.NavigationControl());

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Initialize Mapbox Geocoder
  useEffect(() => {
    if (!map.current || !geocoderContainer.current) return;

    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      placeholder: "Enter Address",
    });

    geocoderContainer.current.appendChild(geocoder.onAdd(map.current));

    geocoder.on("result", (e) => {
      const { text, geometry } = e.result;
      const [longitude, latitude] = geometry.coordinates;

      setFormValues((prev) => ({
        ...prev,
        address: text,
        latitude,
        longitude,
      }));

      if (marker.current) marker.current.remove();
      marker.current = new mapboxgl.Marker()
        .setLngLat([longitude, latitude])
        .addTo(map.current);

      map.current.flyTo({ center: [longitude, latitude], zoom: 14 });

      updateGeofenceCircle(latitude, longitude);
    });

    return () => {
      if (geocoderContainer.current) {
        while (geocoderContainer.current.firstChild) {
          geocoderContainer.current.removeChild(geocoderContainer.current.firstChild);
        }
      }
    };
  }, []);

  // Function to update geofence circle
  const updateGeofenceCircle = (latitude, longitude) => {
    if (!map.current) return;

    if (map.current.getLayer("geofence-circle")) {
      map.current.removeLayer("geofence-circle");
    }
    if (map.current.getSource("geofence")) {
      map.current.removeSource("geofence");
    }

    if (!formValues.geofence_enabled || !formValues.geofence_radius) {
      return;
    }

    const radius = parseFloat(formValues.geofence_radius);
    if (isNaN(radius)) return;

    map.current.addSource("geofence", {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [longitude, latitude],
        },
      },
    });

    map.current.addLayer({
      id: "geofence-circle",
      type: "circle",
      source: "geofence",
      paint: {
        "circle-radius": {
          stops: [
            [0, 0],
            [20, radius],
          ],
          base: 2,
        },
        "circle-color": "#007bff",
        "circle-opacity": 0.3,
        "circle-stroke-color": "#007bff",
        "circle-stroke-width": 2,
      },
    });
  };

  // Update geofence circle when geofence_enabled or geofence_radius changes
  useEffect(() => {
    if (formValues.latitude && formValues.longitude && map.current) {
      updateGeofenceCircle(formValues.latitude, formValues.longitude);
    }
  }, [formValues.geofence_enabled, formValues.geofence_radius]);

  // Validation Logic
  const validateForm = () => {
    const newErrors = {};

    if (!formValues.name) {
      newErrors.name = "Name is required";
    }
    if (!formValues.address) {
      newErrors.address = "Address is required";
    }
    if (!formValues.timezone) {
      newErrors.timezone = "Timezone is required";
    }
    if (formValues.geofence_enabled && !formValues.geofence_radius) {
      newErrors.geofence_radius = "Geofence radius is required when geofencing is enabled";
    }
    if (formValues.geofence_enabled && formValues.geofence_radius <= 0) {
      newErrors.geofence_radius = "Geofence radius must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const user = JSON.parse(localStorage.getItem("user"));
    const payload = {
      title: formValues.name,
      address: formValues.address,
      timezone: formValues.timezone,
      geofence_enabled: formValues.geofence_enabled ? 1 : 0,
      geofence_radius: formValues.geofence_radius || "",
      latitude: formValues.latitude || "",
      longitude: formValues.longitude || "",
      org_id: user?.id,
    };

    try {
      const response = await axios.post(`${API_URL_LOCAL}/api/company/create`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 201) {
        setAlert({ show: true, type: "success", message: "Location added successfully!" });
        setFormValues({
          name: "",
          address: "",
          timezone: "",
          geofence_enabled: false,
          geofence_radius: "",
          latitude: null,
          longitude: null,
        });
        setErrors({});
        if (marker.current) marker.current.remove();
        if (map.current?.getLayer("geofence-circle")) {
          map.current.removeLayer("geofence-circle");
        }
        if (map.current?.getSource("geofence")) {
          map.current.removeSource("geofence");
        }
        if (geocoderContainer.current) {
          const input = geocoderContainer.current.querySelector("input");
          if (input) input.value = "";
        }
        if (map.current) {
          map.current.flyTo({ center: [-74.0060, 40.7128], zoom: 10 });
        }
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      setAlert({ show: true, type: "error", message: "Failed to submit form. Please try again." });
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Add Location" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <div className="row justify-content-center">
            <div className="col-xxl-6 col-xl-8 col-lg-10">
              <div className="card border">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <h6 className="mb-20">Location Details</h6>
                    <FormInput
                      label="Name"
                      name="name"
                      type="text"
                      value={formValues.name}
                      onChange={(e) =>
                        setFormValues((prev) => ({ ...prev, name: e.target.value }))
                      }
                      error={errors.name}
                      required
                      placeholder="Enter Location Name"
                    />
                    <div className="mb-20">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Address <span className="text-danger-600">*</span>
                      </label>
                      <div ref={geocoderContainer} className="radius-8"></div>
                      {errors.address && <small className="text-danger-600">{errors.address}</small>}
                    </div>
                    <FormSelect
                      label="Timezone"
                      name="timezone"
                      value={formValues.timezone}
                      onChange={(e) =>
                        setFormValues((prev) => ({ ...prev, timezone: e.target.value }))
                      }
                      options={timezoneOptions}
                      error={errors.timezone}
                      required
                    />
                    <div className="mb-20">
                      
                      <div className="form-switch switch-success d-flex align-items-center gap-3 ">
                       <div className="col-md-6">
                       <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Enable Geofencing
                      </label>
                      
                       
                       </div>
                       <div className="col-md-6 text-end " style={{paddingRight: 20}}>
                       <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="switch1"
                                name="geofence_enabled"
                                onChange={(e) =>
                                  setFormValues((prev) => ({
                                    ...prev,
                                    geofence_enabled: e.target.checked,
                                    geofence_radius: e.target.checked ? prev.geofence_radius : "",
                                  }))
                                }
                                checked={formValues.geofence_enabled}
                            />
                       </div>
                      </div>
                    </div>
                    {formValues.geofence_enabled && (
                      <FormInput
                        label="Geofence Radius (meters)"
                        name="geofence_radius"
                        type="number"
                        value={formValues.geofence_radius}
                        onChange={(e) =>
                          setFormValues((prev) => ({ ...prev, geofence_radius: e.target.value }))
                        }
                        error={errors.geofence_radius}
                        required
                        placeholder="Enter radius in meters"
                      />
                    )}
                    <div className="mb-20">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Location Map
                      </label>
                      <div
                        ref={mapContainer}
                        style={{ height: "400px", borderRadius: "8px" }}
                      ></div>
                    </div>
                    <div className="d-flex justify-content-center">
                      <button
                        type="submit"
                        className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                      >
                        Submit
                      </button>
                    </div>
                    {alert.show && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className={`alert alert-${alert.type} bg-${alert.type}-50 text-${alert.type}-600 border-${alert.type}-50 px-24 py-11 mt-20 fw-semibold text-lg radius-8 d-flex align-items-center justify-content-between`}
                      >
                        {alert.message}
                        <button
                          type="button"
                          onClick={() => setAlert({ show: false, type: "", message: "" })}
                          className="text-secondary-600 text-xxl"
                        >
                          <Icon icon="iconamoon:sign-times-light" />
                        </button>
                      </motion.div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default AddLocationPage;